/**
 * One form control, chosen by the hint's `widget`. Labels, help text and errors are
 * wired for assistive technology (aria-describedby / aria-invalid), so every form
 * gets that right without any per-form effort.
 *
 * A control that needs more than `register` can give it (the directory picker) is
 * supplied through `render`, which receives the same id and ARIA wiring the built-in
 * controls get, so the label, help text and error markup stay in one place.
 *
 * `current` is a value the field already has somewhere else — on the directory user
 * being updated — shown beneath the box so the submitter can see what they are
 * changing. It is never put *into* the box: a blank box still means "unchanged".
 */
export function Field({ hint, registration, error, render, current }) {
  const id = `field-${hint.name}`;
  const helpId = hint.helpText ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const currentId = current !== undefined ? `${id}-current` : undefined;
  const describedBy = [helpId, currentId, errorId].filter(Boolean).join(' ') || undefined;

  const common = {
    id,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
    placeholder: hint.placeholder,
    ...registration,
  };

  let control;
  if (render) {
    control = render({ id, describedBy, invalid: Boolean(error), placeholder: hint.placeholder });
  } else {
    switch (hint.widget) {
      case 'textarea':
        control = <textarea rows={5} {...common} />;
        break;
      case 'select':
        control = (
          <select {...common}>
            <option value="">Choose…</option>
            {(hint.options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
        break;
      case 'date':
        control = <input type="date" {...common} />;
        break;
      case 'checkbox':
        control = <input type="checkbox" {...common} />;
        break;
      case 'email':
      case 'directory-user':
        control = <input type="email" autoComplete="email" {...common} />;
        break;
      case 'tel':
        // A phone keypad on mobile; the renderer tidies the value to E.164 on blur.
        control = <input type="tel" autoComplete="tel" inputMode="tel" {...common} />;
        break;
      default:
        control = <input type="text" {...common} />;
    }
  }

  const classes = [
    'field',
    hint.widget === 'checkbox' ? 'field--checkbox' : '',
    error ? 'field--invalid' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <label htmlFor={id}>{hint.label}</label>
      {control}
      {hint.helpText && (
        <p id={helpId} className="help">
          {hint.helpText}
        </p>
      )}
      {current !== undefined && (
        <p id={currentId} className="current">
          Currently: {current}
        </p>
      )}
      {error && (
        <p id={errorId} className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
